import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRolePath } from '../../../../app/routes/useRolePath';
import { paths } from '../../../../app/routes/paths';
import type { SearchEntityType, SearchHit } from '../../application/types';
import { useLazySearchQuery } from '../../infrastructure/api/search.api';

const DEFAULT_LIMIT = 15;

export function useSearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const to = useRolePath();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trigger, { data, isFetching, isError }] = useLazySearchQuery();

  const q = searchParams.get('q') ?? '';
  const typeParam = searchParams.get('type');
  const type: SearchEntityType | undefined =
    typeParam === 'mentors' || typeParam === 'students' ? typeParam : undefined;
  const page = Math.max(1, Number(searchParams.get('page') || '1') || 1);

  const [inputValue, setInputValue] = useState(q);

  useEffect(() => {
    setInputValue(q);
  }, [q]);

  useEffect(() => {
    if (!q.trim()) return;
    void trigger({
      q: q.trim(),
      type,
      page,
      limit: DEFAULT_LIMIT,
      mode: 'full',
    });
  }, [q, type, page, trigger]);

  const results: SearchHit[] = data?.list ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const onSubmit = useCallback(
    (value: string) => {
      const next = value.trim();
      const params = new URLSearchParams();
      if (next) params.set('q', next);
      if (type) params.set('type', type);
      params.set('page', '1');
      setSearchParams(params);
    },
    [setSearchParams, type],
  );

  const onTypeChange = useCallback(
    (nextType: SearchEntityType | undefined) => {
      const params = new URLSearchParams(searchParams);
      if (nextType) params.set('type', nextType);
      else params.delete('type');
      params.set('page', '1');
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const onPageChange = useCallback(
    (nextPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('page', String(nextPage));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const onResultClick = useCallback(
    (hit: SearchHit) => {
      if (hit.link) {
        navigate(hit.link);
        return;
      }
      if (hit.category?.key === 'mentors' && hit.slug) {
        navigate(to(`/mentors/${encodeURIComponent(hit.slug)}`));
      }
    },
    [navigate, to],
  );

  const typeOptions = useMemo(
    () => [
      { value: undefined as SearchEntityType | undefined, label: t('search.type.all') },
      { value: 'mentors' as const, label: t('search.type.mentors') },
      { value: 'students' as const, label: t('search.type.students') },
    ],
    [t],
  );

  return {
    t,
    q,
    inputValue,
    setInputValue,
    type,
    page,
    results,
    total,
    totalPages,
    isLoading: isFetching,
    isError,
    onSubmit,
    onTypeChange,
    onPageChange,
    onResultClick,
    typeOptions,
    searchPath: to(paths.search),
  };
}
